import React, { Suspense, lazy, useEffect, ReactNode } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import Card from '../../components/bootstrap/Card';
import PageWrapper from '../PageWrapper/PageWrapper';
import Page from '../Page/Page';
import SubHeader from '../SubHeader/SubHeader';
import Spinner from '../../components/bootstrap/Spinner';
import contents from "../../routes/contentRoutes";

const PAGE_404 = lazy(() => import("../../pages/presentation/auth/Page404"));

const LOADING: React.ReactNode = (
  <PageWrapper>
    <SubHeader>
      <div />
    </SubHeader>
    <Page>
      <div className='d-flex justify-content-center align-items-center h-100'>
        <Spinner size={300} color={'primary'} isGrow={true} />
      </div>
    </Page>
  </PageWrapper>
);

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_: Error): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error("Caught an error:", error, errorInfo);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <Card>
          <h2>Something went wrong.</h2>
          <button onClick={() => window.location.reload()}>Reload page</button>
        </Card>
      );
    }

    return this.props.children;
  }
}

interface BlankPageDetectorProps {
  children: ReactNode;
}

const BlankPageDetector: React.FC<BlankPageDetectorProps> = ({ children }) => {
  const location = useLocation();

  useEffect(() => {
    const timer = setTimeout(() => {
      if (document.body.innerHTML.trim().length === 0) {
        console.log("Blank page detected. Reloading...");
        window.location.reload();
      }
    }, 1000); 

    return () => clearTimeout(timer);
  }, [location]);

  return <>{children}</>;
}

interface RouteConfig {
  path: string;
  element: React.ReactNode;
  caseSensitive?: boolean;
  index?: boolean;
}

const EnhancedContent: React.FC = () => {
  return (
    <ErrorBoundary>
      <BlankPageDetector>
        <main className='content'>
          <Suspense fallback={LOADING}>
            <Routes>
              {(contents as RouteConfig[]).map((page) => {
                const { path, element, caseSensitive, index } = page;
                return index ? (
                  <Route key={path} index element={element} caseSensitive={caseSensitive} />
                ) : (
                  <Route key={path} path={path} element={element} caseSensitive={caseSensitive} />
                );
              })}
              <Route path="*" element={<PAGE_404 />} />
            </Routes>
          </Suspense>
        </main>
      </BlankPageDetector>
    </ErrorBoundary>
  );
};

export default EnhancedContent;